import { addDays, differenceInCalendarDays, getISODay } from 'date-fns';

import {
    type AgentGenerationSelection,
    type SpendBucketSize,
    type SpendDateRange,
    type SpendMode,
} from '@apps/web/components/admin/llm_spend/constants';
import {
    type LlmProviderBillingSection,
    type LlmSpendBucket,
    type LlmSpendDashboardResponse,
    type LlmSpendOutreachGenerationMetrics,
    type LlmSpendSchedulerBucket,
    type LlmSpendSchedulerGenerationMetrics,
    type LlmSpendTotals,
} from '@libraries/typescript/api_client';

const SURFACE_SHARES = {
    outreach_agent: 0.62,
    scheduler_chat: 0.12,
    inline_reviewer: 0.09,
    sms_engine: 0.07,
    llm_judge: 0.05,
    voice_qa: 0.03,
    call_analysis: 0.01,
    other: 0.01,
};
const COMPONENT_SHARES = {
    cache_write: 0.6,
    cache_read: 0.12,
    input: 0.12,
    output: 0.16,
};
const MODEL_SHARES = {
    'claude-sonnet-4-5': 0.7,
    'claude-haiku-4-5': 0.2,
    'claude-opus-4-1': 0.1,
};
const SOURCE_SHARES = {
    agent_turns: 0.83,
    swarm_decisions: 0.02,
    voice_call_audit_runs: 0.13,
    voice_calls: 0.02,
};
const OUTREACH_OBJECTIVE_SHARES = {
    kickoff: 0.12,
    reply_handling: 0.14,
    retry_round: 0.05,
    add_caregivers: 0.24,
    auto_start: 0.08,
    monitor_check: 0.08,
    recommendation_rerun: 0.04,
    fill_shift: 0.05,
    remove_caregivers: 0.03,
    start_stop: 0.07,
    save_memory: 0.05,
    trigger_ehr_sync: 0.02,
    coordinator_message_with_action: 0.03,
};
const SCHEDULER_OBJECTIVE_SHARES = {
    draft_outreach: 0.2,
    review_shifts: 0.15,
    review_clients: 0.1,
    review_outreach: 0.1,
    caregiver_recommendations: 0.08,
    add_caregivers: 0.08,
    update_shift: 0.07,
    contact_caregiver: 0.06,
    save_memory: 0.05,
    ehr_note: 0.04,
    scheduling_rules: 0.03,
    answer_question: 0.02,
    coordinator_message_with_action: 0.02,
};
const ORGANIZATIONS = [
    ['Harbor Home Care', 'Harbor - East'],
    ['Maple Grove Caregivers', 'Maple Grove'],
    ['Sunrise Senior Support', 'Sunrise - NYC'],
    ['Bluebird Home Health', 'Bluebird'],
    ['Cedar Valley Care', 'Cedar Valley'],
    ['Willow Creek Home Care', 'Willow Creek'],
    ['Evergreen Companion Care', 'Evergreen'],
    ['Golden Hour Care', 'Golden Hour'],
    ['Northstar Home Support', 'Northstar'],
    ['Magnolia Family Care', 'Magnolia'],
] as const;

function mulberry32(seed: number): () => number {
    let state = seed;
    return () => {
        state = (state + 0x6d2b79f5) | 0;
        let value = Math.imul(state ^ (state >>> 15), 1 | state);
        value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
        return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
    };
}

function isoDate(day: Date): string {
    return day.toISOString().slice(0, 10);
}

function split(
    total: number,
    shares: Readonly<Record<string, number>>,
): Record<string, number> {
    return Object.fromEntries(
        Object.entries(shares).map(([key, share]) => [key, total * share]),
    );
}

function modelComponents(
    total: number,
): Record<string, Record<string, number>> {
    return Object.fromEntries(
        Object.entries(MODEL_SHARES).map(([model, share]) => [
            model,
            split(total * share, COMPONENT_SHARES),
        ]),
    );
}

// V3 slowly takes share from V2 across the range and runs a little cheaper
// per outreach, so the comparison charts have something to say.
function outreachByGeneration(
    outreachCount: number,
    outreachSpend: number,
    dayIndex: number,
): Record<string, LlmSpendOutreachGenerationMetrics> {
    const v3Share = Math.min(0.82, 0.6 + dayIndex * 0.003);
    const v3Count = Math.round(outreachCount * v3Share);
    const v2Count = outreachCount - v3Count;
    const v3Spend = outreachSpend * v3Share * 0.82;
    const v2Spend = outreachSpend - v3Spend;
    return {
        v2: {
            outreach_count: v2Count,
            tracked_estimated_spend_usd: v2Spend,
            llm_call_count: Math.round(v2Count * 12.4),
            wake_count: Math.round(v2Count * 4.8),
            spend_by_objective: split(v2Spend, OUTREACH_OBJECTIVE_SHARES),
            wakes_by_objective: split(
                Math.round(v2Count * 4.8),
                OUTREACH_OBJECTIVE_SHARES,
            ),
        },
        v3: {
            outreach_count: v3Count,
            tracked_estimated_spend_usd: v3Spend,
            llm_call_count: Math.round(v3Count * 10.9),
            wake_count: Math.round(v3Count * 4.4),
            spend_by_objective: split(v3Spend, OUTREACH_OBJECTIVE_SHARES),
            wakes_by_objective: split(
                Math.round(v3Count * 4.4),
                OUTREACH_OBJECTIVE_SHARES,
            ),
        },
    };
}

function sampleDay(
    day: Date,
    dayIndex: number,
    random: () => number,
): LlmSpendBucket {
    const weekendFactor = getISODay(day) >= 6 ? 0.55 : 1;
    const spend = 2_850 * weekendFactor * (0.9 + random() * 0.2);
    const outreachCount = Math.round(520 * weekendFactor);
    const outreachSpend = spend * SURFACE_SHARES.outreach_agent;
    return {
        bucket_start: isoDate(day),
        tracked_estimated_spend_usd: spend,
        input_tokens: Math.round(spend * 32_000),
        output_tokens: Math.round(spend * 2_500),
        cache_read_input_tokens: Math.round(spend * 95_000),
        cache_creation_input_tokens: Math.round(spend * 42_000),
        spend_by_surface: split(spend, SURFACE_SHARES),
        spend_by_component: split(spend, COMPONENT_SHARES),
        spend_by_model_component: modelComponents(spend),
        spend_by_flow: {
            outreach: outreachSpend,
            scheduler: spend * 0.12,
            evaluation: spend * 0.18,
            other: spend * 0.08,
        },
        spend_by_source: split(spend, SOURCE_SHARES),
        spend_by_objective: split(outreachSpend, OUTREACH_OBJECTIVE_SHARES),
        wakes_by_objective: split(
            Math.round(outreachCount * 4.8),
            OUTREACH_OBJECTIVE_SHARES,
        ),
        outreach_by_generation: outreachByGeneration(
            outreachCount,
            outreachSpend,
            dayIndex,
        ),
        unattributed_spend_usd: spend * SURFACE_SHARES.sms_engine,
        outreach_count: outreachCount,
        filled_outreach_count: Math.round(outreachCount * 0.22),
        filled_shift_count: Math.round(outreachCount * 0.25),
        outreach_spend_usd: outreachSpend,
        outreach_llm_call_count: Math.round(outreachCount * 12.4),
        outreach_wake_count: Math.round(outreachCount * 4.8),
        tracked_organization_count: Math.round(110 * weekendFactor),
        tracked_user_count: Math.round(250 * weekendFactor),
        active_organization_count: Math.round(110 * weekendFactor),
        active_user_count: Math.round(250 * weekendFactor),
    };
}

function sumRecords(
    records: ReadonlyArray<Readonly<Record<string, number>>>,
): Record<string, number> {
    const result: Record<string, number> = {};
    for (const record of records) {
        for (const [key, value] of Object.entries(record)) {
            result[key] = (result[key] ?? 0) + value;
        }
    }
    return result;
}

function sumModelComponents(
    records: ReadonlyArray<
        Readonly<Record<string, Readonly<Record<string, number>>>>
    >,
): Record<string, Record<string, number>> {
    const models = new Set(records.flatMap((record) => Object.keys(record)));
    return Object.fromEntries(
        [...models].map((model) => [
            model,
            sumRecords(records.map((record) => record[model] ?? {})),
        ]),
    );
}

function sumOutreachGenerations(
    records: ReadonlyArray<
        Readonly<Record<string, LlmSpendOutreachGenerationMetrics>>
    >,
): Record<string, LlmSpendOutreachGenerationMetrics> {
    const generations = new Set(
        records.flatMap((record) => Object.keys(record)),
    );
    return Object.fromEntries(
        [...generations].map((generation) => {
            const rows = records.flatMap((record) => {
                const metrics = record[generation];
                return metrics === undefined ? [] : [metrics];
            });
            return [
                generation,
                {
                    outreach_count: rows.reduce(
                        (sum, row) => sum + row.outreach_count,
                        0,
                    ),
                    tracked_estimated_spend_usd: rows.reduce(
                        (sum, row) => sum + row.tracked_estimated_spend_usd,
                        0,
                    ),
                    llm_call_count: rows.reduce(
                        (sum, row) => sum + row.llm_call_count,
                        0,
                    ),
                    wake_count: rows.reduce(
                        (sum, row) => sum + row.wake_count,
                        0,
                    ),
                    spend_by_objective: sumRecords(
                        rows.map((row) => row.spend_by_objective),
                    ),
                    wakes_by_objective: sumRecords(
                        rows.map((row) => row.wakes_by_objective ?? {}),
                    ),
                },
            ];
        }),
    );
}

function sampleProviderBilling(
    days: ReadonlyArray<LlmSpendBucket>,
): LlmProviderBillingSection {
    // Shares and the billed-over-estimate factor are invented; the sample
    // only has to show a provider total that never equals the estimate.
    const providerDefinitions = [
        ['openai', 0.18, 'sample-openai-project'],
        ['anthropic', 0.42, 'sample-anthropic-workspace'],
        ['google', 0.3, 'sample-vertex-project'],
        ['aws', 0.1, 'Claude via Bedrock'],
    ] as const;
    const estimate = days.reduce(
        (sum, day) => sum + day.tracked_estimated_spend_usd,
        0,
    );
    const providers = providerDefinitions.map(([provider, share, label]) => {
        const amount = (estimate * share * 1.025).toFixed(6);
        return {
            provider,
            covered_from: days[0]?.bucket_start ?? null,
            covered_through: days[days.length - 1]?.bucket_start ?? null,
            has_preliminary: provider === 'aws',
            totals: [{ currency: 'USD', amount }],
            projects: [{ label, currency: 'USD', amount }],
        };
    });
    return {
        scope_note:
            'Provider totals are global across live, sandbox, CI, eval, and uninstrumented activity. Organization and mode filters apply only to the estimated spend.',
        totals: [
            {
                currency: 'USD',
                amount: providers
                    .reduce(
                        (sum, provider) =>
                            sum + Number(provider.totals[0]?.amount ?? 0),
                        0,
                    )
                    .toFixed(6),
            },
        ],
        providers,
    };
}

function aggregateBuckets(days: ReadonlyArray<LlmSpendBucket>): LlmSpendBucket {
    const first = days[0];
    if (first === undefined) {
        throw new Error('A bucket needs at least one day');
    }
    const sum = (field: keyof LlmSpendTotals) =>
        days.reduce((total, day) => total + day[field], 0);
    return {
        bucket_start: first.bucket_start,
        tracked_estimated_spend_usd: sum('tracked_estimated_spend_usd'),
        input_tokens: sum('input_tokens'),
        output_tokens: sum('output_tokens'),
        cache_read_input_tokens: sum('cache_read_input_tokens'),
        cache_creation_input_tokens: sum('cache_creation_input_tokens'),
        unattributed_spend_usd: sum('unattributed_spend_usd'),
        outreach_count: sum('outreach_count'),
        filled_outreach_count: sum('filled_outreach_count'),
        filled_shift_count: sum('filled_shift_count'),
        outreach_spend_usd: sum('outreach_spend_usd'),
        outreach_llm_call_count: sum('outreach_llm_call_count'),
        outreach_wake_count: sum('outreach_wake_count'),
        tracked_organization_count: Math.round(
            Math.max(...days.map((day) => day.tracked_organization_count)) *
                1.4,
        ),
        tracked_user_count: Math.round(
            Math.max(...days.map((day) => day.tracked_user_count)) * 1.4,
        ),
        active_organization_count: Math.round(
            Math.max(...days.map((day) => day.active_organization_count)) * 1.4,
        ),
        active_user_count: Math.round(
            Math.max(...days.map((day) => day.active_user_count)) * 1.4,
        ),
        spend_by_surface: sumRecords(days.map((day) => day.spend_by_surface)),
        spend_by_component: sumRecords(
            days.map((day) => day.spend_by_component),
        ),
        spend_by_model_component: sumModelComponents(
            days.map((day) => day.spend_by_model_component),
        ),
        spend_by_flow: sumRecords(days.map((day) => day.spend_by_flow)),
        spend_by_source: sumRecords(days.map((day) => day.spend_by_source)),
        spend_by_objective: sumRecords(
            days.map((day) => day.spend_by_objective),
        ),
        wakes_by_objective: sumRecords(
            days.map((day) => day.wakes_by_objective ?? {}),
        ),
        outreach_by_generation: sumOutreachGenerations(
            days.map((day) => day.outreach_by_generation),
        ),
    };
}

function totalsFrom(days: ReadonlyArray<LlmSpendBucket>): LlmSpendTotals {
    const totals = aggregateBuckets(days);
    return {
        tracked_estimated_spend_usd: totals.tracked_estimated_spend_usd,
        input_tokens: totals.input_tokens,
        output_tokens: totals.output_tokens,
        cache_read_input_tokens: totals.cache_read_input_tokens,
        cache_creation_input_tokens: totals.cache_creation_input_tokens,
        unattributed_spend_usd: totals.unattributed_spend_usd,
        outreach_count: totals.outreach_count,
        filled_outreach_count: totals.filled_outreach_count,
        filled_shift_count: totals.filled_shift_count,
        outreach_spend_usd: totals.outreach_spend_usd,
        outreach_llm_call_count: totals.outreach_llm_call_count,
        outreach_wake_count: totals.outreach_wake_count,
        tracked_organization_count: 158,
        tracked_user_count: 435,
        active_organization_count: 158,
        active_user_count: 435,
    };
}

function sampleDays(
    range: SpendDateRange,
    seed: number,
): Array<LlmSpendBucket> {
    const start = new Date(`${range.start}T00:00:00Z`);
    const dayCount = differenceInCalendarDays(
        new Date(`${range.end}T00:00:00Z`),
        start,
    );
    const random = mulberry32(seed);
    return Array.from({ length: dayCount }, (_unused, index) =>
        sampleDay(addDays(start, index), index, random),
    );
}

function generationMultipliers(generation: AgentGenerationSelection): {
    readonly count: number;
    readonly cost: number;
    readonly calls: number;
    readonly wakes: number;
} {
    if (generation === 'v2') {
        return { count: 0.36, cost: 0.36, calls: 0.36, wakes: 0.36 };
    }
    if (generation === 'v3') {
        return {
            count: 0.64,
            cost: 0.64 * 0.82,
            calls: 0.64 * 0.88,
            wakes: 0.64 * 0.91,
        };
    }
    return {
        count: 1,
        cost: 0.36 + 0.64 * 0.82,
        calls: 0.36 + 0.64 * 0.88,
        wakes: 0.36 + 0.64 * 0.91,
    };
}

function filterOutreachBucket(
    row: LlmSpendBucket,
    generation: AgentGenerationSelection,
): LlmSpendBucket {
    const multipliers = generationMultipliers(generation);
    return {
        ...row,
        outreach_count: Math.round(row.outreach_count * multipliers.count),
        outreach_spend_usd: row.outreach_spend_usd * multipliers.cost,
        outreach_llm_call_count: Math.round(
            row.outreach_llm_call_count * multipliers.calls,
        ),
        outreach_wake_count: Math.round(
            row.outreach_wake_count * multipliers.wakes,
        ),
        spend_by_objective: Object.fromEntries(
            Object.entries(row.spend_by_objective).map(([key, value]) => [
                key,
                value * multipliers.cost,
            ]),
        ),
        wakes_by_objective: Object.fromEntries(
            Object.entries(row.wakes_by_objective ?? {}).map(([key, value]) => [
                key,
                value * multipliers.wakes,
            ]),
        ),
        outreach_by_generation: Object.fromEntries(
            Object.entries(row.outreach_by_generation).filter(
                ([key]) => generation === 'all' || key === generation,
            ),
        ),
    };
}

function schedulerGeneration(
    spend: number,
    conversations: number,
    callsPerConversation: number,
    wakesPerConversation: number,
): LlmSpendSchedulerGenerationMetrics {
    return {
        tracked_estimated_spend_usd: spend,
        conversation_count: conversations,
        llm_call_count: Math.round(conversations * callsPerConversation),
        wake_count: Math.round(conversations * wakesPerConversation),
        spend_by_objective: split(spend, SCHEDULER_OBJECTIVE_SHARES),
        wakes_by_objective: split(
            Math.round(conversations * wakesPerConversation),
            SCHEDULER_OBJECTIVE_SHARES,
        ),
    };
}

function sumSchedulerBuckets(
    week: ReadonlyArray<LlmSpendSchedulerGenerationMetrics>,
): LlmSpendSchedulerGenerationMetrics {
    return {
        tracked_estimated_spend_usd: week.reduce(
            (sum, row) => sum + row.tracked_estimated_spend_usd,
            0,
        ),
        conversation_count: week.reduce(
            (sum, row) => sum + row.conversation_count,
            0,
        ),
        llm_call_count: week.reduce((sum, row) => sum + row.llm_call_count, 0),
        wake_count: week.reduce((sum, row) => sum + row.wake_count, 0),
        spend_by_objective: sumRecords(
            week.map((row) => row.spend_by_objective),
        ),
        wakes_by_objective: sumRecords(
            week.map((row) => row.wakes_by_objective ?? {}),
        ),
    };
}

function schedulerBucketsFrom(
    days: ReadonlyArray<LlmSpendBucket>,
    bucket: SpendBucketSize,
    generation: AgentGenerationSelection,
): Array<LlmSpendSchedulerBucket> {
    const daily = days.map((day) => {
        const spend = day.spend_by_surface['scheduler_chat'] ?? 0;
        const conversations = Math.max(
            1,
            Math.round(day.outreach_count * 0.35),
        );
        const v3Conversations = Math.round(conversations * 0.45);
        const byGeneration = {
            v2: schedulerGeneration(
                spend * 0.6,
                conversations - v3Conversations,
                6.4,
                2.7,
            ),
            v3: schedulerGeneration(spend * 0.4, v3Conversations, 5.1, 2.2),
        };
        const selected = Object.fromEntries(
            Object.entries(byGeneration).filter(
                ([key]) => generation === 'all' || key === generation,
            ),
        );
        return {
            ...sumSchedulerBuckets(Object.values(selected)),
            bucket_start: day.bucket_start,
            by_generation: selected,
        } satisfies LlmSpendSchedulerBucket;
    });
    if (bucket === 'day') {
        return daily;
    }
    return Array.from(
        { length: Math.ceil(daily.length / 7) },
        (_unused, index) => daily.slice(index * 7, index * 7 + 7),
    )
        .filter((week) => week.length > 0)
        .map((week) => ({
            ...sumSchedulerBuckets(week),
            bucket_start: week[0]?.bucket_start ?? '',
            by_generation: Object.fromEntries(
                ['v2', 'v3']
                    .filter((key) => generation === 'all' || key === generation)
                    .map((key) => [
                        key,
                        sumSchedulerBuckets(
                            week.flatMap((row) => {
                                const metrics = row.by_generation[key];
                                return metrics === undefined ? [] : [metrics];
                            }),
                        ),
                    ]),
            ),
        }));
}

export function buildSampleLlmSpendDashboard(
    range: SpendDateRange,
    bucket: SpendBucketSize,
    mode: SpendMode,
    generation: AgentGenerationSelection = 'all',
): LlmSpendDashboardResponse {
    const rawDays = sampleDays(range, 20_260_909);
    const days = rawDays.map((row) => filterOutreachBucket(row, generation));
    const previousRawDays = sampleDays(
        {
            start: isoDate(
                addDays(new Date(`${range.start}T00:00:00Z`), -days.length),
            ),
            end: range.start,
        },
        20_260_701,
    );
    const previousDays = previousRawDays.map((row) =>
        filterOutreachBucket(row, generation),
    );
    const totals = totalsFrom(days);
    const weekly = Array.from(
        { length: Math.ceil(days.length / 7) },
        (_unused, index) => days.slice(index * 7, index * 7 + 7),
    ).filter((week) => week.length > 0);
    const schedulerBuckets = schedulerBucketsFrom(rawDays, bucket, generation);
    const schedulerSpend = schedulerBuckets.reduce(
        (sum, row) => sum + row.tracked_estimated_spend_usd,
        0,
    );
    const schedulerConversations = schedulerBuckets.reduce(
        (sum, row) => sum + row.conversation_count,
        0,
    );
    const schedulerCalls = schedulerBuckets.reduce(
        (sum, row) => sum + row.llm_call_count,
        0,
    );
    const schedulerWakes = schedulerBuckets.reduce(
        (sum, row) => sum + row.wake_count,
        0,
    );
    const organizationShares = [
        0.2, 0.12, 0.09, 0.07, 0.05, 0.045, 0.04, 0.035, 0.03, 0.025,
    ];
    const selectedModes: Array<'live' | 'sandbox'> =
        mode === 'combined' ? ['live', 'sandbox'] : [mode];
    const organizationRows = ORGANIZATIONS.map(
        ([name, internalName], index) => {
            const share = organizationShares[index] ?? 0.04;
            return {
                organization_id: `00000000-0000-4000-8000-${String(200_000_000_000 + index).slice(-12)}`,
                organization_name: name,
                organization_internal_name: internalName,
                tracked_estimated_spend_usd:
                    totals.tracked_estimated_spend_usd * share,
                outreach_spend_usd: totals.outreach_spend_usd * share,
                outreach_count: Math.round(totals.outreach_count * share),
                filled_outreach_count: Math.round(
                    totals.filled_outreach_count * share,
                ),
                filled_shift_count: Math.round(
                    totals.filled_shift_count * share,
                ),
            };
        },
    );
    const users = [
        ['team@phoebe.work', true],
        ['system@phoebe.work', true],
        ['jamie@harbor.example', false],
        ['priya@maplegrove.example', false],
        ['marcus@sunrise.example', false],
        ['elena@bluebird.example', false],
        ['sam@cedarvalley.example', false],
        ['avery@willowcreek.example', false],
        ['riley@evergreen.example', false],
        ['jordan@goldenhour.example', false],
        ['taylor@northstar.example', false],
        ['morgan@magnolia.example', false],
    ] as const;
    const userShares = [
        0.1, 0.085, 0.075, 0.068, 0.061, 0.055, 0.049, 0.044, 0.039, 0.034,
        0.029, 0.024,
    ];
    const userRows = users.map(([email, automation], index) => ({
        user_id: `00000000-0000-4000-8000-${String(300_000_000_000 + index).slice(-12)}`,
        email,
        organization_name:
            ORGANIZATIONS[index % ORGANIZATIONS.length]?.[1] ??
            ORGANIZATIONS[0][1],
        tracked_estimated_spend_usd:
            totals.tracked_estimated_spend_usd * (userShares[index] ?? 0.02),
        llm_call_count: Math.round(7_500 - index * 450),
        is_automation: automation,
    }));

    return {
        range_start: range.start,
        range_end: range.end,
        bucket,
        mode,
        rollup_as_of: `${range.end}T06:10:00Z`,
        coverage: {
            included_sources: [
                'agent turns',
                'caregiver recommendation evaluations',
                'voice QA audits',
                'realtime voice LLM usage',
            ],
            missing_sources: [],
            native_history_complete: true,
            modes: selectedModes.map((selectedMode) => ({
                mode: selectedMode,
                history_start: '2026-05-22T00:00:00Z',
                source_watermark: `${range.end}T05:55:00Z`,
                last_completed_at: `${range.end}T06:10:00Z`,
                native_history_start: '2026-05-22T00:00:00Z',
                native_source_watermark: `${range.end}T05:55:00Z`,
                native_last_completed_at: `${range.end}T06:10:00Z`,
            })),
        },
        totals,
        previous_totals: totalsFrom(previousDays),
        buckets: bucket === 'week' ? weekly.map(aggregateBuckets) : days,
        outreach_statuses: [
            ['cancelled', 0.18, 0.18, 2.1, 0.4],
            ['filled', 0.22, 0.32, 1.3, 1.3],
            ['ended_with_fill', 0.33, 0.44, 1.8, 1.2],
            ['ended_without_fill', 0.42, 0.34, 1.9, 0],
        ].map(([status, countShare, spendShare, shiftsPerOutreach, fills]) => {
            const outreachCount = Math.round(
                totals.outreach_count * Number(countShare),
            );
            return {
                status: String(status),
                outreach_count: outreachCount,
                shift_count: Math.round(
                    outreachCount * Number(shiftsPerOutreach),
                ),
                filled_shift_count: Math.round(outreachCount * Number(fills)),
                tracked_estimated_spend_usd:
                    totals.outreach_spend_usd * Number(spendShare),
            };
        }),
        outreach_response_outcomes: (
            [
                ['yes', 0.38, 0.42, 1.4, 1.1],
                ['responded_no_yes', 0.41, 0.35, 1.3, 0],
                ['no_response', 0.17, 0.19, 0, 0],
                ['untracked', 0.04, 0.04, 0, 0],
            ] as const
        ).map(([outcome, countShare, spendShare, responses, yeses]) => {
            const outreachCount = Math.round(
                totals.outreach_count * Number(countShare),
            );
            return {
                outcome,
                outreach_count: outreachCount,
                shift_count: Math.round(outreachCount * 1.6),
                responded_shift_count: Math.round(
                    outreachCount * Number(responses),
                ),
                yes_shift_count: Math.round(outreachCount * Number(yeses)),
                tracked_estimated_spend_usd:
                    totals.outreach_spend_usd * Number(spendShare),
            };
        }),
        scheduler_totals: {
            tracked_estimated_spend_usd: schedulerSpend,
            conversation_count: schedulerConversations,
            llm_call_count: schedulerCalls,
            wake_count: schedulerWakes,
        },
        scheduler_buckets: schedulerBuckets,
        scheduler_top_organizations: organizationRows.map((row, index) => ({
            organization_id: row.organization_id,
            organization_name: row.organization_name,
            organization_internal_name: row.organization_internal_name,
            tracked_estimated_spend_usd:
                schedulerSpend * (organizationShares[index] ?? 0.04),
            conversation_count: Math.round(
                schedulerConversations * (organizationShares[index] ?? 0.04),
            ),
            llm_call_count: Math.round(
                schedulerCalls * (organizationShares[index] ?? 0.04),
            ),
            wake_count: Math.round(
                schedulerWakes * (organizationShares[index] ?? 0.04),
            ),
        })),
        scheduler_top_users: userRows.map((row, index) => ({
            ...row,
            tracked_estimated_spend_usd:
                schedulerSpend * (userShares[index] ?? 0.02),
            conversation_count: Math.round(
                schedulerConversations * (userShares[index] ?? 0.02),
            ),
            llm_call_count: Math.round(
                schedulerCalls * (userShares[index] ?? 0.02),
            ),
            wake_count: Math.round(
                schedulerWakes * (userShares[index] ?? 0.02),
            ),
        })),
        scheduler_outliers: [42, 23, 14].map((cost, index) => ({
            conversation_id: `00000000-0000-4000-8000-${String(400_000_000_000 + index).slice(-12)}`,
            organization_name: ORGANIZATIONS[index]?.[1] ?? '',
            tracked_estimated_spend_usd: cost,
            llm_call_count: Math.round(cost * 1.5),
            wake_count: Math.round(cost * 0.7),
            started_at: addDays(
                new Date(`${range.start}T13:00:00Z`),
                index,
            ).toISOString(),
        })),
        top_organizations: organizationRows,
        top_users: userRows,
        outliers: [111, 86, 48, 29, 16, 13].map((cost, index) => {
            const status =
                ['cancelled', 'unfilled', 'filled'][index % 3] ?? 'cancelled';
            return {
                conversation_id: `00000000-0000-4000-8000-${String(100_000_000_000 + index).slice(-12)}`,
                organization_name:
                    ORGANIZATIONS[index % ORGANIZATIONS.length]?.[1] ?? '',
                tracked_estimated_spend_usd: cost,
                llm_call_count: Math.round(cost * 1.7),
                cost_capped: cost >= 100,
                status,
                outcome: status,
                started_at: addDays(
                    new Date(`${range.start}T13:00:00Z`),
                    index,
                ).toISOString(),
            };
        }),
        providers: sampleProviderBilling(rawDays),
    };
}
